import { DatabaseBlockDataSource } from '@blocksuite/affine/blocks/database';

import {
  collectDatabaseRootSubdocIdsFromDatabaseBlocks,
  collectDatabaseSubdocIdsFromDatabaseBlocks,
  collectDatabaseSubdocIdsFromDataSourceRefs,
  collectInitialDatabaseSubdocIdsFromDatabaseBlocks,
  collectLocalFirstDatabaseSubdocIdsFromDatabaseBlocks,
  DATABASE_BLOCK_FLAVOUR,
  type DatabaseSubdocBlockIdentity,
  type DatabaseSubdocDataSourceRef,
  DEFAULT_DATABASE_ROW_SHARD_ID,
  isDatabaseRootDocId,
  isDiscoverableDatabaseSubdocIdentity,
  toDatabaseDocId,
} from './database-subdoc-identities';
export {
  collectDatabaseRootSubdocIdsFromDatabaseBlocks,
  collectDatabaseSubdocIdsFromDatabaseBlocks,
  collectDatabaseSubdocIdsFromDataSourceRefs,
  collectInitialDatabaseSubdocIdsFromDatabaseBlocks,
  collectLocalFirstDatabaseSubdocIdsFromDatabaseBlocks,
  type DatabaseSubdocBlockIdentity,
  type DatabaseSubdocDataSourceRef,
} from './database-subdoc-identities';
import type { Store } from '@blocksuite/affine/store';
import {
  createDatabaseShardManifest,
  createDatabaseShardManifestEntry,
  createServerDatabaseRootDocId,
  createServerDatabaseRowShardDocId,
  normalizeDatabaseBlockId,
  normalizeDatabaseShardManifest,
  parseServerDatabaseRowShardDocId,
} from '@streamlain/database-identity';
import { Map as YMap } from 'yjs';

import type { WorkspaceService } from '../../workspace';
import { collectSnapshotExportBlobIds } from './database-subdoc-export-blobs';

export type DatabaseSystemDocAccessBinding = {
  databaseId: string;
  rootDocId: string;
  accessDocId: string;
  blockId: string;
  manifest: NonNullable<ReturnType<typeof normalizeDatabaseShardManifest>>;
};

type DatabaseSubdocPageDiscoverySnapshot = {
  blockCount: number;
  databaseBlockCount: number;
  databaseBlocksWithId: number;
  databaseBlocksWithManifest: number;
  databaseBlocksWithRootDocId: number;
  malformedBlockCount: number;
  sampleDatabaseBlocks: Array<{
    hasDatabaseId: boolean;
    hasManifest: boolean;
    hasRootDocId: boolean;
  }>;
  sampleFlavours: string[];
};

const DATABASE_LOAD_MARK_PREFIX = 'sl-database-load';
const DATABASE_DISCOVERY_SAMPLE_LIMIT = 5;
const SNAPSHOT_EXPORT_DATABASE_SUBDOC_LOAD_TIMEOUT_MS = 15_000;
const ACTIVE_DATABASE_SUBDOC_PRIORITY = 110;

export type SnapshotExportMode =
  | 'single-doc'
  | 'multi-doc'
  | 'workspace'
  | 'recovery-current-state';

type DatabaseSubdocDocEngine = {
  addDoc(
    docId: string,
    source: 'database-subdoc' | 'workspace-snapshot-export'
  ): void;
  addPriority(docId: string, priority: number): () => void;
  debugDocState(docId: string): Promise<Array<{ reasons: string[] }>>;
  flushAndWaitForSynced?(docId: string, abort?: AbortSignal): Promise<void>;
  pullRemote?(docId: string): Promise<void>;
  waitForDocLoaded(docId: string, abort?: AbortSignal): Promise<void>;
  waitForDocReady?(docId: string, abort?: AbortSignal): Promise<void>;
  waitForSynced?(docId?: string, abort?: AbortSignal): Promise<void>;
  waitForUpdated?(docId?: string, abort?: AbortSignal): Promise<void>;
  releaseDoc(
    docId: string,
    source: 'workspace-doc-load' | 'workspace-snapshot-export'
  ): Promise<void>;
};

type DatabaseSubdocExportLoadPlan = {
  eagerDocIds: Set<string>;
  emptyRowShardDocIds: Set<string>;
  rootDocIds: Set<string>;
  rowShardCandidateGroups: string[][];
};

export type ExportScopePlan = {
  mode: SnapshotExportMode;
  sourceDocIds: string[];
  eagerDocIds: string[];
  rootDocIds: string[];
  rowShardCandidateGroups: string[][];
  requiredDocIds: string[];
  blobIds: string[];
  warnings: string[];
  timeoutMs: number;
};

function markDatabaseLoad(event: string, data: Record<string, unknown>): void {
  if (
    typeof performance === 'undefined' ||
    typeof performance.mark !== 'function'
  ) {
    return;
  }

  try {
    performance.mark(
      `${DATABASE_LOAD_MARK_PREFIX}:${event}:${JSON.stringify(data)}`
    );
  } catch {
    // Performance marks are diagnostics only.
  }
}

function loadDatabaseWorkspaceDoc(
  page: Store,
  docId: string,
  reason: string
): void {
  const workspace = page.workspace as {
    createDoc?: (id: string) => { load?: () => unknown; ready?: boolean };
    getDoc?: (
      id: string
    ) => { load?: () => unknown; ready?: boolean } | null | undefined;
    registerDoc?: (id: string) => unknown;
  };
  let doc = workspace.getDoc?.(docId);
  if (!doc) {
    workspace.registerDoc?.(docId);
    doc = workspace.getDoc?.(docId);
  }
  if (!doc) {
    doc = workspace.createDoc?.(docId);
  }

  if (doc && !doc.ready) {
    doc.load?.();
  }

  markDatabaseLoad('preload-workspace-doc-load', {
    docId,
    pageId: page.id,
    reason,
    workspaceId: page.workspace.id,
  });
}

/**
 * Selects the bounded database working set that follows an opened PageDoc into
 * the existing local DocStorage. DocStorage persists every connected subdoc,
 * so reopening a cached PageDoc can hydrate these rows locally before remote
 * reconciliation. The global visible-row limit prevents a large database from
 * being materialized wholesale merely because its host document was opened.
 */

function createDatabaseSubdocExportLoadPlan(): DatabaseSubdocExportLoadPlan {
  return {
    eagerDocIds: new Set(),
    emptyRowShardDocIds: new Set(),
    rootDocIds: new Set(),
    rowShardCandidateGroups: [],
  };
}

function addCandidateGroup(
  groups: string[][],
  candidates: Iterable<unknown>
): void {
  const unique = new Set<string>();
  for (const candidate of candidates) {
    const docId = toDatabaseDocId(candidate);
    if (docId) {
      unique.add(docId);
    }
  }
  if (unique.size > 0) {
    groups.push(Array.from(unique));
  }
}

function addDefaultServerDocIdHints(
  plan: DatabaseSubdocExportLoadPlan,
  workspaceId: string,
  databaseId: unknown
): string | null {
  if (typeof databaseId !== 'string' || !databaseId) {
    return null;
  }

  try {
    const rootDocId = createServerDatabaseRootDocId({
      databaseId,
      workspaceId,
    });
    plan.eagerDocIds.add(rootDocId);
    plan.eagerDocIds.add(
      createServerDatabaseRowShardDocId({
        databaseId,
        shardId: DEFAULT_DATABASE_ROW_SHARD_ID,
        workspaceId,
      })
    );
    return rootDocId;
  } catch {
    return null;
  }
}

function addDatabaseIdentitiesToExportLoadPlan(
  plan: DatabaseSubdocExportLoadPlan,
  identities: Iterable<DatabaseSubdocBlockIdentity>,
  workspaceId: string
): void {
  for (const block of identities) {
    if (!isDiscoverableDatabaseSubdocIdentity(block)) {
      continue;
    }

    const fallbackRootDocId = addDefaultServerDocIdHints(
      plan,
      workspaceId,
      block.databaseId
    );
    let hasRequiredRootDocId = false;
    const explicitRootDocId = toDatabaseDocId(block.databaseRootDocId);
    if (explicitRootDocId) {
      plan.eagerDocIds.add(explicitRootDocId);
      plan.rootDocIds.add(explicitRootDocId);
      hasRequiredRootDocId = true;
    }

    try {
      const manifest = normalizeDatabaseShardManifest(
        block.databaseShardManifest
      );
      const manifestRootDocId = toDatabaseDocId(manifest?.databaseRootDocId);
      if (manifestRootDocId) {
        plan.eagerDocIds.add(manifestRootDocId);
        plan.rootDocIds.add(manifestRootDocId);
        hasRequiredRootDocId = true;
      }
      if (manifest) {
        for (const shard of manifest.shards) {
          if (shard.rowCount <= 0) {
            // Empty shards carry no row payload, so they cannot satisfy the
            // content-readiness wait. They still need a registered Store so
            // the archive remains manifest-complete for snapshot import.
            plan.eagerDocIds.add(shard.serverDocId);
            plan.emptyRowShardDocIds.add(shard.serverDocId);
            continue;
          }
          addCandidateGroup(plan.rowShardCandidateGroups, [shard.serverDocId]);
        }
      }
    } catch {
      // Corrupt manifest data must not block deterministic root/default preload.
    }

    if (!hasRequiredRootDocId && fallbackRootDocId) {
      plan.rootDocIds.add(fallbackRootDocId);
    }
  }
}

function collectDatabaseSubdocExportLoadPlanFromPage(
  page: Store
): DatabaseSubdocExportLoadPlan {
  const { identities } = readDatabaseSubdocPageDiscovery(page);
  const plan = createDatabaseSubdocExportLoadPlan();
  addDatabaseIdentitiesToExportLoadPlan(plan, identities, page.workspace.id);
  return plan;
}

function mergeDatabaseSubdocExportLoadPlan(
  target: DatabaseSubdocExportLoadPlan,
  source: DatabaseSubdocExportLoadPlan
): void {
  source.eagerDocIds.forEach(docId => target.eagerDocIds.add(docId));
  source.emptyRowShardDocIds.forEach(docId =>
    target.emptyRowShardDocIds.add(docId)
  );
  source.rootDocIds.forEach(docId => target.rootDocIds.add(docId));
  target.rowShardCandidateGroups.push(...source.rowShardCandidateGroups);
}

function dedupeCandidateGroups(groups: string[][]): string[][] {
  const seen = new Set<string>();
  const result: string[][] = [];
  for (const group of groups) {
    const normalized = Array.from(new Set(group)).sort();
    if (normalized.length === 0) {
      continue;
    }
    const key = normalized.join('\0');
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(normalized);
  }
  return result;
}

function waitForDatabaseDocReady(
  engineDoc: DatabaseSubdocDocEngine,
  docId: string,
  abort?: AbortSignal
): Promise<void> {
  return engineDoc.waitForDocReady
    ? engineDoc.waitForDocReady(docId, abort)
    : engineDoc.waitForDocLoaded(docId, abort);
}

function waitForAnyDocReady(
  engineDoc: DatabaseSubdocDocEngine,
  candidateDocIds: readonly string[],
  timeoutMs: number
): Promise<string> {
  if (candidateDocIds.length === 0) {
    return Promise.reject(new Error('No database subdoc candidates to load.'));
  }

  const abort = new AbortController();
  let settled = false;
  let remaining = candidateDocIds.length;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return new Promise((resolve, reject) => {
    const finish = (callback: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      abort.abort();
      callback();
    };

    timeoutId = setTimeout(() => {
      finish(() => {
        reject(
          new Error(
            `Cannot export sharded database because none of these database docs loaded within ${timeoutMs}ms: ${candidateDocIds.join(', ')}`
          )
        );
      });
    }, timeoutMs);

    for (const docId of candidateDocIds) {
      waitForDatabaseDocReady(engineDoc, docId, abort.signal)
        .then(() => {
          finish(() => resolve(docId));
        })
        .catch(() => {
          if (settled) {
            return;
          }
          remaining -= 1;
          if (remaining === 0) {
            finish(() => {
              reject(
                new Error(
                  `Cannot export sharded database because none of these database docs loaded: ${candidateDocIds.join(', ')}`
                )
              );
            });
          }
        });
    }
  });
}

async function loadDatabaseSubdocGroupsForSnapshotExport(
  engineDoc: DatabaseSubdocDocEngine,
  groups: readonly string[][],
  timeoutMs: number
): Promise<void> {
  for (const group of groups) {
    await waitForAnyDocReady(engineDoc, group, timeoutMs);
  }
}

export async function prepareDatabaseSubdocsForSnapshotExport(
  pages: readonly Store[],
  workspaceService: WorkspaceService,
  options: {
    timeoutMs?: number;
    mode?: SnapshotExportMode;
    workspaceStream?: {
      claimDocId(docId: string): Promise<boolean>;
      onStoreReady(store: Store): Promise<void>;
    };
  } = {}
): Promise<ExportScopePlan> {
  const timeoutMs =
    options.timeoutMs ?? SNAPSHOT_EXPORT_DATABASE_SUBDOC_LOAD_TIMEOUT_MS;
  const mode = options.mode ?? 'single-doc';
  const engineDoc = workspaceService.workspace.engine.doc;
  const cleanups: Array<() => void> = [];
  const enqueued = new Set<string>();
  const initiallyWorkspaceLoadRequested = new Set<string>();
  const plan = createDatabaseSubdocExportLoadPlan();
  const requestSource =
    mode === 'workspace' ? 'workspace-snapshot-export' : 'database-subdoc';

  for (const page of pages) {
    mergeDatabaseSubdocExportLoadPlan(
      plan,
      collectDatabaseSubdocExportLoadPlanFromPage(page)
    );
  }

  if (mode === 'workspace' && options.workspaceStream) {
    const sourcePage = pages[0];
    const consume = async (
      docId: string,
      waitForReady = true
    ): Promise<Store | null> => {
      if (!(await options.workspaceStream!.claimDocId(docId))) return null;
      const states = await engineDoc.debugDocState(docId);
      const initiallyHadWorkspaceLoadRequest = states.some(state =>
        state.reasons.includes('request:workspace-doc-load')
      );
      engineDoc.addDoc(docId, 'workspace-snapshot-export');
      const releasePriority = engineDoc.addPriority(docId, 95);
      try {
        if (sourcePage) {
          loadDatabaseWorkspaceDoc(sourcePage, docId, 'snapshot-export');
        }
        if (waitForReady) {
          await waitForAnyDocReady(engineDoc, [docId], timeoutMs);
        }
        const store = sourcePage?.workspace
          .getDoc(docId)
          ?.getStore({ id: docId });
        if (!store) {
          throw new Error(
            `Cannot export database doc because it has no loaded store: ${docId}`
          );
        }
        await options.workspaceStream!.onStoreReady(store);
        return store;
      } finally {
        releasePriority();
        await engineDoc.releaseDoc(docId, 'workspace-snapshot-export');
        if (!initiallyHadWorkspaceLoadRequest) {
          await engineDoc.releaseDoc(docId, 'workspace-doc-load');
        }
      }
    };

    for (const rootDocId of plan.rootDocIds) {
      const rootStore = await consume(rootDocId);
      if (rootStore) {
        mergeDatabaseSubdocExportLoadPlan(
          plan,
          collectDatabaseSubdocExportLoadPlanFromPage(rootStore)
        );
      }
    }
    for (const docId of plan.emptyRowShardDocIds) {
      await consume(docId, false);
    }
    for (const group of dedupeCandidateGroups(plan.rowShardCandidateGroups)) {
      let loaded = false;
      let lastError: unknown;
      for (const docId of group) {
        try {
          loaded = (await consume(docId)) !== null || loaded;
          if (loaded) break;
        } catch (error) {
          lastError = error;
        }
      }
      if (!loaded && lastError) throw lastError;
    }
    return toExportScopePlan(mode, pages, plan, timeoutMs);
  }

  const enqueue = async (docId: string) => {
    if (enqueued.has(docId)) {
      return;
    }
    enqueued.add(docId);
    if (mode === 'workspace') {
      const states = await engineDoc.debugDocState(docId);
      if (
        states.some(state =>
          state.reasons.includes('request:workspace-doc-load')
        )
      ) {
        initiallyWorkspaceLoadRequested.add(docId);
      }
    }
    engineDoc.addDoc(docId, requestSource);
    cleanups.push(engineDoc.addPriority(docId, 95));
    const sourcePage = pages[0];
    if (sourcePage) {
      loadDatabaseWorkspaceDoc(sourcePage, docId, 'snapshot-export');
    }
  };

  try {
    // Source stores are already loaded: callers obtained them from the active
    // workspace before entering this preflight. Waiting for their network
    // engine state makes an otherwise local snapshot export depend on backend
    // availability. Only discovered database subdocs need hydration here.
    for (const docId of plan.eagerDocIds) await enqueue(docId);
    await loadDatabaseSubdocGroupsForSnapshotExport(
      engineDoc,
      Array.from(plan.rootDocIds, docId => [docId]),
      timeoutMs
    );

    for (const rootDocId of plan.rootDocIds) {
      const rootStore = pages[0]?.workspace.getDoc(rootDocId)?.getStore({
        id: rootDocId,
      });
      if (!rootStore) {
        continue;
      }
      mergeDatabaseSubdocExportLoadPlan(
        plan,
        collectDatabaseSubdocExportLoadPlanFromPage(rootStore)
      );
    }

    for (const docId of plan.eagerDocIds) await enqueue(docId);
    for (const group of plan.rowShardCandidateGroups) {
      for (const docId of group) await enqueue(docId);
    }
    await loadDatabaseSubdocGroupsForSnapshotExport(
      engineDoc,
      dedupeCandidateGroups(plan.rowShardCandidateGroups),
      timeoutMs
    );
    return toExportScopePlan(mode, pages, plan, timeoutMs);
  } finally {
    cleanups.splice(0).forEach(cleanup => cleanup());
    if (mode === 'workspace') {
      for (const docId of enqueued) {
        await engineDoc.releaseDoc(docId, 'workspace-snapshot-export');
        if (!initiallyWorkspaceLoadRequested.has(docId)) {
          await engineDoc.releaseDoc(docId, 'workspace-doc-load');
        }
      }
    }
  }
}

function toExportScopePlan(
  mode: SnapshotExportMode,
  pages: readonly Store[],
  plan: DatabaseSubdocExportLoadPlan,
  timeoutMs: number
): ExportScopePlan {
  const sourceDocIds = Array.from(new Set(pages.map(page => page.id))).sort();
  const eagerDocIds = Array.from(plan.eagerDocIds).sort();
  const rootDocIds = Array.from(plan.rootDocIds).sort();
  const rowShardCandidateGroups = dedupeCandidateGroups(
    plan.rowShardCandidateGroups
  );
  const requiredDocIds = Array.from(
    new Set([
      ...sourceDocIds,
      ...eagerDocIds,
      ...rowShardCandidateGroups.flat(),
    ])
  ).sort();
  const blobIds = collectSnapshotExportBlobIds(
    pages,
    rootDocIds,
    rowShardCandidateGroups.flat()
  );

  return {
    mode,
    sourceDocIds,
    eagerDocIds,
    rootDocIds,
    rowShardCandidateGroups,
    requiredDocIds,
    blobIds,
    warnings: [],
    timeoutMs,
  };
}

function addSampleFlavour(sampleFlavours: string[], flavour: unknown): void {
  if (
    typeof flavour !== 'string' ||
    sampleFlavours.includes(flavour) ||
    sampleFlavours.length >= DATABASE_DISCOVERY_SAMPLE_LIMIT
  ) {
    return;
  }
  sampleFlavours.push(flavour);
}

function readDatabaseSubdocPageDiscovery(page: Store): {
  identities: DatabaseSubdocBlockIdentity[];
  snapshot: DatabaseSubdocPageDiscoverySnapshot;
} {
  const blocks = page.spaceDoc.getMap('blocks') as YMap<YMap<unknown>>;
  const identities: DatabaseSubdocBlockIdentity[] = [];
  const snapshot: DatabaseSubdocPageDiscoverySnapshot = {
    blockCount: blocks.size,
    databaseBlockCount: 0,
    databaseBlocksWithId: 0,
    databaseBlocksWithManifest: 0,
    databaseBlocksWithRootDocId: 0,
    malformedBlockCount: 0,
    sampleDatabaseBlocks: [],
    sampleFlavours: [],
  };

  for (const [blockId, block] of blocks.entries()) {
    if (!(block instanceof YMap)) {
      snapshot.malformedBlockCount += 1;
      continue;
    }
    const identity: DatabaseSubdocBlockIdentity = {
      id: block.get('sys:id') ?? blockId,
      databaseId: block.get('prop:databaseId'),
      databaseRootDocId: block.get('prop:databaseRootDocId'),
      databaseShardManifest: block.get('prop:databaseShardManifest'),
      flavour: block.get('sys:flavour'),
      managedBy: block.get('prop:managedBy'),
      visible: block.get('prop:visible'),
    };
    addSampleFlavour(snapshot.sampleFlavours, identity.flavour);
    identities.push(identity);

    if (!isDiscoverableDatabaseSubdocIdentity(identity)) {
      continue;
    }

    snapshot.databaseBlockCount += 1;
    const hasDatabaseId = typeof identity.databaseId === 'string';
    const hasRootDocId = typeof identity.databaseRootDocId === 'string';
    const hasManifest = identity.databaseShardManifest != null;
    if (hasDatabaseId) {
      snapshot.databaseBlocksWithId += 1;
    }
    if (hasRootDocId) {
      snapshot.databaseBlocksWithRootDocId += 1;
    }
    if (hasManifest) {
      snapshot.databaseBlocksWithManifest += 1;
    }
    if (
      snapshot.sampleDatabaseBlocks.length < DATABASE_DISCOVERY_SAMPLE_LIMIT
    ) {
      snapshot.sampleDatabaseBlocks.push({
        hasDatabaseId,
        hasManifest,
        hasRootDocId,
      });
    }
  }

  return {
    identities,
    snapshot,
  };
}

export function inspectDatabaseSubdocDiscoveryFromPage(
  page: Store
): DatabaseSubdocPageDiscoverySnapshot {
  return readDatabaseSubdocPageDiscovery(page).snapshot;
}

export function collectDatabaseSubdocIdsFromPage(page: Store): string[] {
  const { identities } = readDatabaseSubdocPageDiscovery(page);

  return collectDatabaseSubdocIdsFromDatabaseBlocks(
    identities,
    page.workspace.id
  );
}

export function collectDatabaseSystemDocAccessBindingsFromPage(
  page: Store,
  resolveStore?: (docId: string) => Store | null,
  requestedDatabaseIds?: ReadonlySet<string>
): DatabaseSystemDocAccessBinding[] {
  const bindings: DatabaseSystemDocAccessBinding[] = [];

  for (const block of page.getBlocksByFlavour(DATABASE_BLOCK_FLAVOUR)) {
    const props = block.model.props as Record<string, unknown>;
    const databaseIdValue = props.databaseId ?? block.model.id;
    const databaseId =
      typeof databaseIdValue === 'string'
        ? normalizeDatabaseBlockId(databaseIdValue)
        : null;
    if (!databaseId) continue;
    if (requestedDatabaseIds && !requestedDatabaseIds.has(databaseId)) {
      continue;
    }

    const rootDocIdValue = props.databaseRootDocId;
    const rootDocId =
      typeof rootDocIdValue === 'string' && rootDocIdValue
        ? rootDocIdValue
        : createServerDatabaseRootDocId({
            workspaceId: page.workspace.id,
            databaseId,
          });
    const rootStore = resolveStore?.(rootDocId);
    const rootBlock = rootStore
      ?.getBlocksByFlavour(DATABASE_BLOCK_FLAVOUR)
      .find(candidate => {
        const candidateProps = candidate.model.props as Record<string, unknown>;
        return (
          candidate.model.id === databaseId ||
          candidateProps.databaseId === databaseId
        );
      });
    const rootProps = rootBlock?.model.props as
      | Record<string, unknown>
      | undefined;
    const currentManifest = normalizeDatabaseShardManifest(
      rootProps?.databaseShardManifest ?? props.databaseShardManifest
    );
    const manifest = createDatabaseShardManifest({
      workspaceId: page.workspace.id,
      databaseId,
      databaseRootDocId: rootDocId,
      targetBytes: currentManifest?.targetBytes,
      hardLimitBytes: currentManifest?.hardLimitBytes,
      shards: currentManifest?.shards.length
        ? currentManifest.shards
        : [
            createDatabaseShardManifestEntry({
              workspaceId: page.workspace.id,
              databaseId,
              shardId: DEFAULT_DATABASE_ROW_SHARD_ID,
              firstRowId: undefined,
              lastRowId: undefined,
              rowCount: 0,
              encodedBytes: 0,
              status: 'ready',
            }),
          ],
    });

    bindings.push({
      databaseId,
      rootDocId,
      accessDocId: page.id,
      blockId: block.model.id,
      manifest,
    });
  }

  return bindings;
}

export function collectDatabaseRootSubdocIdsFromPage(page: Store): string[] {
  const { identities } = readDatabaseSubdocPageDiscovery(page);

  return collectDatabaseRootSubdocIdsFromDatabaseBlocks(
    identities,
    page.workspace.id
  );
}

export function prioritizeDatabaseSubdocsForBlock(
  page: Store,
  blockId: string,
  workspaceService: WorkspaceService
): () => void {
  const block = page.getBlock(blockId);
  if (!block || block.model.flavour !== DATABASE_BLOCK_FLAVOUR) {
    return () => {};
  }

  const props = block.model.props as Record<string, unknown>;
  const docIds = collectInitialDatabaseSubdocIdsFromDatabaseBlocks(
    [
      {
        id: block.model.id,
        databaseId: props.databaseId ?? block.model.id,
        databaseRootDocId: props.databaseRootDocId,
        databaseShardManifest: props.databaseShardManifest,
        flavour: block.model.flavour,
        managedBy: props.managedBy,
        visible: props.visible,
      },
    ],
    page.workspace.id
  );
  const engineDoc = workspaceService.workspace.engine.doc;
  const cleanups: Array<() => void> = [];

  for (const docId of docIds) {
    cleanups.push(
      engineDoc.addPriority(docId, ACTIVE_DATABASE_SUBDOC_PRIORITY)
    );
    engineDoc.addDoc(docId, 'database-subdoc');
    loadDatabaseWorkspaceDoc(page, docId, 'active-database');
  }

  markDatabaseLoad('preload-active-database', {
    blockId,
    count: docIds.length,
    pageId: page.id,
    workspaceId: page.workspace.id,
  });

  return () => cleanups.splice(0).forEach(cleanup => cleanup());
}

export function prioritizeDatabaseRowShardForFocus(
  page: Store,
  blockId: string,
  rowShardDocId: string,
  workspaceService: WorkspaceService,
  onReady: () => void
): () => void {
  const block = page.getBlock(blockId);
  if (!block || block.model.flavour !== DATABASE_BLOCK_FLAVOUR) {
    throw new Error(
      `Cannot focus database row shard without its database block: block=${blockId} shard=${rowShardDocId}`
    );
  }

  const workspaceId = page.workspace.id;
  if (workspaceService.workspace.id !== workspaceId) {
    throw new Error(
      `Database focus workspace mismatch: page=${workspaceId} service=${workspaceService.workspace.id}`
    );
  }
  const props = block.model.props as Record<string, unknown>;
  const databaseIdValue = props.databaseId ?? block.model.id;
  const databaseId =
    typeof databaseIdValue === 'string'
      ? normalizeDatabaseBlockId(databaseIdValue)
      : null;
  const parsedRowShard = parseServerDatabaseRowShardDocId(
    rowShardDocId,
    workspaceId
  );
  if (!databaseId || parsedRowShard?.databaseId !== databaseId) {
    throw new Error(
      `Database focus row shard locator is stale: block=${blockId} shard=${rowShardDocId}`
    );
  }

  const engineDoc = workspaceService.workspace.engine.doc;
  const abort = new AbortController();
  engineDoc.addDoc(rowShardDocId, 'database-subdoc');
  const cleanupPriority = engineDoc.addPriority(
    rowShardDocId,
    ACTIVE_DATABASE_SUBDOC_PRIORITY
  );
  loadDatabaseWorkspaceDoc(page, rowShardDocId, 'database-focus');

  void waitForDatabaseDocReady(engineDoc, rowShardDocId, abort.signal)
    .then(() => {
      if (!abort.signal.aborted) {
        onReady();
      }
    })
    .catch(error => {
      if (abort.signal.aborted) {
        return;
      }
      console.error(
        `Failed to hydrate database focus shard "${rowShardDocId}"`,
        error
      );
    });

  return () => {
    abort.abort();
    cleanupPriority();
  };
}

function collectInitialDatabaseSubdocIdsAndDiscoveryFromPage(page: Store): {
  discovery: DatabaseSubdocPageDiscoverySnapshot;
  localFirstSubdocIds: string[];
  subdocIds: string[];
} {
  const { identities, snapshot } = readDatabaseSubdocPageDiscovery(page);

  return {
    discovery: snapshot,
    localFirstSubdocIds: collectLocalFirstDatabaseSubdocIdsFromDatabaseBlocks(
      identities,
      page.workspace.id
    ),
    subdocIds: collectInitialDatabaseSubdocIdsFromDatabaseBlocks(
      identities,
      page.workspace.id
    ),
  };
}

function markDatabaseSubdocDiscovery(
  page: Store,
  discovery: DatabaseSubdocPageDiscoverySnapshot,
  subdocCount: number
): void {
  markDatabaseLoad('preload-page-discovery', {
    ...discovery,
    pageId: page.id,
    subdocCount,
    workspaceId: page.workspace.id,
  });
}

function observeDatabaseSubdocDiscovery(
  page: Store,
  enqueue: (docId: string, flushReason?: string, priority?: number) => void,
  getEnqueuedCount: () => number
): () => void {
  const blocks = page.spaceDoc.getMap('blocks') as YMap<YMap<unknown>>;
  if (typeof blocks.observe !== 'function') {
    return () => {};
  }

  let marked = false;
  let observing = true;
  const blockObservers = new Map<YMap<unknown>, () => void>();
  const syncBlockObservers = () => {
    const currentBlocks = new Set<YMap<unknown>>();
    for (const block of blocks.values()) {
      if (!(block instanceof YMap)) {
        continue;
      }
      currentBlocks.add(block);
      if (blockObservers.has(block) || typeof block.observe !== 'function') {
        continue;
      }
      const blockObserver = () => observer();
      block.observe(blockObserver);
      blockObservers.set(block, () => block.unobserve(blockObserver));
    }
    for (const [block, dispose] of blockObservers) {
      if (currentBlocks.has(block)) {
        continue;
      }
      dispose();
      blockObservers.delete(block);
    }
  };
  const observer = () => {
    syncBlockObservers();
    const { discovery, localFirstSubdocIds, subdocIds } =
      collectInitialDatabaseSubdocIdsAndDiscoveryFromPage(page);
    if (discovery.databaseBlockCount === 0) {
      return;
    }
    const enqueuedBefore = getEnqueuedCount();
    subdocIds.forEach(docId => enqueue(docId, 'root-discovery'));
    localFirstSubdocIds.forEach(docId => enqueue(docId, undefined, 20));
    const enqueuedAfter = getEnqueuedCount();
    if (!marked) {
      marked = true;
      markDatabaseLoad('preload-page-databases-observed', {
        ...discovery,
        pageId: page.id,
        subdocCount: subdocIds.length,
        workspaceId: page.workspace.id,
      });
    }
    markDatabaseLoad('preload-page-observed-enqueued', {
      count: subdocIds.length,
      enqueuedCount: getEnqueuedCount(),
      newlyEnqueuedCount: enqueuedAfter - enqueuedBefore,
      pageId: page.id,
      workspaceId: page.workspace.id,
    });
  };
  syncBlockObservers();
  blocks.observe(observer);

  return () => {
    if (!observing) {
      return;
    }
    observing = false;
    blocks.unobserve(observer);
    for (const dispose of blockObservers.values()) {
      dispose();
    }
    blockObservers.clear();
  };
}

export function preloadDatabaseSubdocsForPage(
  page: Store,
  workspaceService: WorkspaceService
): () => void {
  const engine = workspaceService.workspace.engine;
  const seen = new Set<string>();
  const flushSeen = new Set<string>();
  const cleanups: Array<() => void> = [];
  let disposed = false;

  markDatabaseLoad('preload-start', {
    pageId: page.id,
    workspaceId: page.workspace.id,
  });

  const waitForLocalDocReady = (docId: string, reason: string) => {
    if (
      typeof engine.doc.waitForDocReady !== 'function' &&
      typeof engine.doc.waitForDocLoaded !== 'function'
    ) {
      return;
    }
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 30_000);
    cleanups.push(() => {
      clearTimeout(timeout);
      abort.abort();
    });
    void waitForDatabaseDocReady(engine.doc, docId, abort.signal)
      .then(() => {
        markDatabaseLoad('preload-subdoc-local-ready-done', {
          docId,
          pageId: page.id,
          reason,
          workspaceId: page.workspace.id,
        });
      })
      .catch(error => {
        if (abort.signal.aborted || disposed) {
          return;
        }
        markDatabaseLoad('preload-subdoc-local-ready-failed', {
          docId,
          error: error instanceof Error ? error.message : String(error),
          pageId: page.id,
          reason,
          workspaceId: page.workspace.id,
        });
      })
      .finally(() => clearTimeout(timeout));
  };

  const enqueue = (
    docId: string,
    flushReason?: string,
    priority: number = 90
  ) => {
    if (disposed || seen.has(docId)) {
      return;
    }
    seen.add(docId);
    engine.doc.addDoc(docId, 'database-subdoc');
    cleanups.push(engine.doc.addPriority(docId, priority));
    const reason = flushReason ?? 'page-open';
    loadDatabaseWorkspaceDoc(page, docId, reason);
    waitForLocalDocReady(docId, reason);
    if (flushReason) {
      flushDatabaseSubdoc(docId, flushReason);
    }
  };

  function flushDatabaseSubdoc(docId: string, reason: string) {
    if (
      disposed ||
      flushSeen.has(docId) ||
      typeof engine.doc.flushAndWaitForSynced !== 'function'
    ) {
      return;
    }
    flushSeen.add(docId);
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 30_000);
    cleanups.push(() => {
      clearTimeout(timeout);
      abort.abort();
    });
    markDatabaseLoad('preload-subdoc-flush-start', {
      docId,
      pageId: page.id,
      reason,
      workspaceId: page.workspace.id,
    });
    void (engine.doc.pullRemote?.(docId) ?? Promise.resolve())
      .then(() => engine.doc.waitForUpdated?.(docId, abort.signal))
      .then(() => waitForDatabaseDocReady(engine.doc, docId, abort.signal))
      .then(() => engine.doc.flushAndWaitForSynced!(docId, abort.signal))
      .then(() => {
        markDatabaseLoad('preload-subdoc-flush-done', {
          docId,
          pageId: page.id,
          reason,
          workspaceId: page.workspace.id,
        });
      })
      .catch(error => {
        if (abort.signal.aborted || disposed) {
          return;
        }
        markDatabaseLoad('preload-subdoc-flush-failed', {
          docId,
          error: error instanceof Error ? error.message : String(error),
          pageId: page.id,
          reason,
          workspaceId: page.workspace.id,
        });
      })
      .finally(() => clearTimeout(timeout));
  }

  const cleanupRowShardDocLoader =
    DatabaseBlockDataSource.registerRowShardDocLoader(page, docId => {
      enqueue(docId, 'row-shard-loader');
    });
  cleanups.push(cleanupRowShardDocLoader);

  const cleanupRowShardSnapshotBarrier =
    DatabaseBlockDataSource.registerRowShardSnapshotBarrier(
      page,
      async ({ rootDocId, rowShardDocIds, signal }) => {
        if (disposed || signal.aborted) {
          throw new Error('Database row-shard snapshot barrier is disposed.');
        }
        if (
          typeof engine.doc.flushAndWaitForSynced !== 'function' ||
          typeof engine.doc.waitForSynced !== 'function'
        ) {
          throw new Error('Database document sync barrier is unavailable.');
        }

        const barrierAbort = new AbortController();
        const abortBarrier = () => barrierAbort.abort();
        signal.addEventListener('abort', abortBarrier, { once: true });
        const timeout = setTimeout(abortBarrier, 30_000);
        const docIds = [...new Set([rootDocId, ...rowShardDocIds])];
        // Temporary bounded GAT diagnostics, no document contents or errors.
        const markBarrier = (phase: string, docId = rootDocId) => {
          const name = `custom-app-sync-barrier:${docId}:${phase}`;
          performance.clearMarks(name);
          performance.mark(name, {
            detail: {
              phase,
              docId,
              rootDocId,
              aborted: barrierAbort.signal.aborted,
            },
          });
        };
        const releasePriorities: Array<() => void> = [];
        try {
          for (const docId of docIds) {
            engine.doc.addDoc(docId, 'database-subdoc');
            releasePriorities.push(engine.doc.addPriority(docId, 110));
            loadDatabaseWorkspaceDoc(
              page,
              docId,
              'row-shard-integrity-validation'
            );
          }
          await Promise.all(
            docIds.map(async docId => {
              markBarrier('pull-start', docId);
              await engine.doc.pullRemote?.(docId);
              markBarrier('pull-done', docId);
              await engine.doc.waitForUpdated?.(docId, barrierAbort.signal);
              markBarrier('updated', docId);
            })
          );
          await Promise.all(
            docIds.map(async docId => {
              await waitForDatabaseDocReady(
                engine.doc,
                docId,
                barrierAbort.signal
              );
              markBarrier('ready', docId);
            })
          );
          await Promise.all(
            docIds.map(async docId => {
              await engine.doc.flushAndWaitForSynced!(
                docId,
                barrierAbort.signal
              );
              markBarrier('flushed', docId);
            })
          );
          markBarrier('global-sync-start');
          await engine.doc.waitForSynced(undefined, barrierAbort.signal);
          markBarrier('global-sync-done');
        } finally {
          markBarrier('finished');
          clearTimeout(timeout);
          signal.removeEventListener('abort', abortBarrier);
          releasePriorities.forEach(release => release());
        }
      }
    );
  cleanups.push(cleanupRowShardSnapshotBarrier);

  const {
    discovery,
    localFirstSubdocIds: pageLocalFirstSubdocIds,
    subdocIds: pageSubdocIds,
  } = collectInitialDatabaseSubdocIdsAndDiscoveryFromPage(page);
  const cleanupDiscoveryObserver = observeDatabaseSubdocDiscovery(
    page,
    enqueue,
    () => seen.size
  );
  markDatabaseSubdocDiscovery(page, discovery, pageSubdocIds.length);
  markDatabaseLoad('preload-page-collected', {
    count: pageSubdocIds.length,
    pageId: page.id,
    workspaceId: page.workspace.id,
  });
  pageSubdocIds.forEach(docId => enqueue(docId));
  pageLocalFirstSubdocIds.forEach(docId => enqueue(docId, undefined, 20));

  engine.indexer
    .getDatabaseSubdocIds(page.id)
    .then(docIds => {
      const rootDocIds = docIds.filter(docId =>
        isDatabaseRootDocId(docId, page.workspace.id)
      );
      markDatabaseLoad('preload-indexer-collected', {
        count: rootDocIds.length,
        disposed,
        enqueuedCount: seen.size,
        rawCount: docIds.length,
        pageId: page.id,
        workspaceId: page.workspace.id,
      });
      rootDocIds.forEach(docId => enqueue(docId));
      markDatabaseLoad('preload-end', {
        disposed,
        enqueuedCount: seen.size,
        indexerCount: rootDocIds.length,
        pageCount: pageSubdocIds.length,
        rawIndexerCount: docIds.length,
        pageId: page.id,
        workspaceId: page.workspace.id,
      });
    })
    .catch(error => {
      markDatabaseLoad('preload-end', {
        disposed,
        enqueuedCount: seen.size,
        error: error instanceof Error ? error.message : 'indexer failed',
        indexerFailed: true,
        pageCount: pageSubdocIds.length,
        pageId: page.id,
        workspaceId: page.workspace.id,
      });
      // Index hints are best-effort; loaded page/root-doc data remains canonical.
    });

  return () => {
    disposed = true;
    cleanupDiscoveryObserver();
    markDatabaseLoad('preload-disposed', {
      enqueuedCount: seen.size,
      pageId: page.id,
      workspaceId: page.workspace.id,
    });
    cleanups.splice(0).forEach(cleanup => cleanup());
  };
}

export function preloadDatabaseSubdocsForDataSourceRefs(
  refs: readonly DatabaseSubdocDataSourceRef[],
  workspaceService: WorkspaceService
): () => void {
  const engine = workspaceService.workspace.engine;
  const docIds = collectDatabaseSubdocIdsFromDataSourceRefs(
    refs,
    workspaceService.workspace.id
  );
  const cleanups: Array<() => void> = [];
  let disposed = false;

  markDatabaseLoad('preload-datasource-refs-start', {
    count: docIds.length,
    refCount: refs.length,
    workspaceId: workspaceService.workspace.id,
  });

  for (const docId of docIds) {
    if (disposed) break;
    engine.doc.addDoc(docId, 'database-subdoc');
    cleanups.push(engine.doc.addPriority(docId, 90));
  }

  return () => {
    disposed = true;
    markDatabaseLoad('preload-datasource-refs-disposed', {
      count: docIds.length,
      workspaceId: workspaceService.workspace.id,
    });
    cleanups.splice(0).forEach(cleanup => cleanup());
  };
}
